import { useState, useEffect, useMemo, useRef } from 'react';

import { useUpdateTransportDateMutation } from '@/hooks/mutations/useTransportDateMutation';
import { useAvailableDatesQuery } from '@/hooks/queries/useAvailableDatesQuery';
import { useToken } from '@/hooks/useToken';
import { dictionaries, useTranslation } from '@/i18n';
import { type TAvailableDates } from '@/types/models';
import { availableDatesToDatesArray } from '@/utils/availableDates';
import { formatDate } from '@/utils/date';

type DrawerState = 'calendar' | 'loading' | 'success' | 'error';

type RescheduleDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  currentDate: string | null;
  mode: 'pickup' | 'delivery';
  trackingNumber: string;
};

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

function formatTimeFrame(hourStart: number, hourEnd: number, name: string) {
  if (name) return name;

  return `${hourStart}:00 - ${hourEnd}:00`;
}

export const RescheduleDrawer = ({
  isOpen,
  onClose,
  currentDate,
  mode,
  trackingNumber,
}: RescheduleDrawerProps) => {
  const today = new Date();
  const initial = currentDate ? new Date(currentDate) : today;
  const { token } = useToken();
  const { dateLocale, locale, t } = useTranslation();
  const calendarDays = dictionaries[locale].reschedule.calendarDays;
  const calendarMonths = dictionaries[locale].reschedule.calendarMonths;
  const modeLabel =
    mode === 'pickup' ? t('reschedule.pickup') : t('reschedule.delivery');
  const initializedRef = useRef(false);

  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);
  const [drawerState, setDrawerState] = useState<DrawerState>('calendar');

  const {
    data: availableDatesResponse,
    isError: isDatesError,
    isLoading: isDatesLoading,
    refetch: refetchDates,
  } = useAvailableDatesQuery({
    enabled: isOpen,
    trackingNumber,
    type: mode,
  });

  const availableDatesMap: TAvailableDates = useMemo(() => {
    if (availableDatesResponse?.success) {
      return availableDatesResponse.dates;
    }

    return {};
  }, [availableDatesResponse?.dates, availableDatesResponse?.success]);

  const availableDates = useMemo(
    () => availableDatesToDatesArray(availableDatesMap),
    [availableDatesMap]
  );

  const selectedDateInfo = selectedDateKey
    ? availableDatesMap[selectedDateKey]
    : undefined;
  const selectedTimeFrame = selectedDateInfo
    ? formatTimeFrame(
        selectedDateInfo.hourStart,
        selectedDateInfo.hourEnd,
        selectedDateInfo.name
      )
    : null;

  const { mutate: updateTransportDate } = useUpdateTransportDateMutation();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setDrawerState('calendar');
    } else {
      document.body.style.overflow = '';
      initializedRef.current = false;
      setSelectedDateKey(null);
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || isDatesLoading || availableDates.length === 0) return;
    if (initializedRef.current) return;

    initializedRef.current = true;

    const matchedDate = currentDate
      ? availableDates.find((date) => formatDate(date) === currentDate)
      : undefined;
    const initialDate = matchedDate ?? availableDates[0];

    setViewYear(initialDate.getFullYear());
    setViewMonth(initialDate.getMonth());
    setSelectedDateKey(formatDate(initialDate));
  }, [isOpen, isDatesLoading, availableDates, currentDate]);

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  };

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const formatSelected = () => {
    if (!selectedDateKey) return '';

    const d = new Date(selectedDateKey);
    return d.toLocaleDateString(dateLocale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const prevDateLabel = currentDate
    ? new Date(currentDate).toLocaleDateString(dateLocale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const isDayAvailable = (day: number) => {
    const key = formatDate(new Date(viewYear, viewMonth, day));
    return Boolean(availableDatesMap[key]);
  };

  const handleSubmit = () => {
    if (!selectedDateKey || !selectedDateInfo) return;

    setDrawerState('loading');

    updateTransportDate(
      {
        date: selectedDateKey,
        token,
        trackingNumber,
      },
      {
        onError: () => {
          setDrawerState('error');
        },
        onSuccess: (response) => {
          if (response.success) {
            setDrawerState('success');
            return;
          }

          setDrawerState('error');
        },
      }
    );
  };

  const footer = (
    <div className='mt-auto pt-6'>
      <div className='rounded-lg bg-gray-50 px-4 py-4'>
        <div className='flex items-start gap-2'>
          <svg
            className='mt-0.5 shrink-0 text-backgroundGreen'
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
          >
            <circle
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='2'
            />
            <path
              d='M12 8v4M12 16h.01'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
            />
          </svg>
          <div className='flex flex-col gap-1 text-xs text-gray-500'>
            <p>
              <span className='font-bold italic text-gray-700'>
                {t('reschedule.footerHeadsUpLabel')}
              </span>
              <span className='italic'> {t('reschedule.footerHeadsUp')}</span>
            </p>
            <p>
              <span className='font-bold italic text-gray-700'>
                {t('reschedule.footerOnceConfirmedLabel')}
              </span>
              <span className='italic'> {t('reschedule.footerConfirmed')}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[9998] bg-black/40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed bottom-0 right-0 top-0 z-[9999] flex w-full max-w-[480px] flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className='flex items-center justify-between px-6 py-5'>
          <h2 className='text-xl font-bold text-gray-900'>
            {t('reschedule.title')}
          </h2>
          <button
            onClick={onClose}
            className='flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600'
          >
            <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
              <path
                d='M18 6L6 18M6 6l12 12'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
              />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className='flex flex-1 flex-col overflow-y-auto px-6 pb-6'>
          {/* CALENDAR STATE */}
          {drawerState === 'calendar' && (
            <>
              {prevDateLabel && (
                <p className='mb-4 text-sm text-gray-600'>
                  {t('reschedule.previousDate', { mode: modeLabel })}{' '}
                  <span className='font-semibold text-gray-900'>
                    {prevDateLabel}
                  </span>
                </p>
              )}

              {isDatesLoading && (
                <div className='flex flex-col items-center justify-center py-16 text-center'>
                  <svg
                    className='animate-spin text-gray-400'
                    width='40'
                    height='40'
                    viewBox='0 0 24 24'
                    fill='none'
                  >
                    <path
                      d='M20 12a8 8 0 1 1-4.93-7.4'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                    />
                  </svg>
                  <p className='mt-4 text-sm text-gray-500'>
                    {t('reschedule.loadingDates', { mode: modeLabel })}
                  </p>
                </div>
              )}

              {isDatesError && !isDatesLoading && (
                <div className='flex flex-col items-center justify-center py-12 text-center'>
                  <p className='text-sm text-gray-500'>
                    {t('reschedule.loadDatesError')}
                  </p>
                  <button
                    onClick={() => refetchDates()}
                    className='mt-4 rounded-md bg-backgroundGreen px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90'
                  >
                    {t('reschedule.retry')}
                  </button>
                </div>
              )}

              {!isDatesLoading &&
                !isDatesError &&
                availableDates.length === 0 && (
                  <div className='flex flex-col items-center justify-center py-12 text-center'>
                    <p className='text-sm text-gray-500'>
                      {mode === 'pickup'
                        ? t('reschedule.pickupNoDates')
                        : t('reschedule.deliveryNoDates')}
                    </p>
                  </div>
                )}

              {!isDatesLoading &&
                !isDatesError &&
                availableDates.length > 0 && (
                  <>
                    {/* Calendar */}
                    <div className='rounded-xl border border-gray-100 bg-white p-4 shadow-sm'>
                      {/* Month header */}
                      <div className='mb-4 flex items-center justify-between'>
                        <span className='text-sm font-semibold text-gray-800'>
                          {calendarMonths[viewMonth]} {viewYear}
                        </span>
                        <div className='flex gap-2'>
                          <button
                            onClick={prevMonth}
                            className='flex h-7 w-7 items-center justify-center rounded-full hover:bg-gray-100'
                          >
                            <svg
                              width='16'
                              height='16'
                              viewBox='0 0 24 24'
                              fill='none'
                            >
                              <path
                                d='M15 18l-6-6 6-6'
                                stroke='#374151'
                                strokeWidth='2'
                                strokeLinecap='round'
                              />
                            </svg>
                          </button>
                          <button
                            onClick={nextMonth}
                            className='flex h-7 w-7 items-center justify-center rounded-full hover:bg-gray-100'
                          >
                            <svg
                              width='16'
                              height='16'
                              viewBox='0 0 24 24'
                              fill='none'
                            >
                              <path
                                d='M9 18l6-6-6-6'
                                stroke='#374151'
                                strokeWidth='2'
                                strokeLinecap='round'
                              />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Day headers */}
                      <div className='mb-2 grid grid-cols-7 text-center'>
                        {calendarDays.map((d) => (
                          <span
                            key={d}
                            className='text-[10px] font-semibold text-gray-400'
                          >
                            {d}
                          </span>
                        ))}
                      </div>

                      {/* Day cells */}
                      <div className='grid grid-cols-7 gap-y-1 text-center'>
                        {Array.from({ length: firstDay }).map((_, i) => (
                          <span key={`e-${i}`} />
                        ))}
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                          const day = i + 1;
                          const dayKey = formatDate(
                            new Date(viewYear, viewMonth, day)
                          );
                          const isAvailable = isDayAvailable(day);
                          const isSelected = dayKey === selectedDateKey;
                          const isToday =
                            day === today.getDate() &&
                            viewMonth === today.getMonth() &&
                            viewYear === today.getFullYear();

                          return (
                            <button
                              key={day}
                              disabled={!isAvailable}
                              onClick={() => setSelectedDateKey(dayKey)}
                              className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors
                          ${isSelected ? 'bg-backgroundGreen text-white' : ''}
                          ${
                            isToday && !isSelected && isAvailable
                              ? 'border border-backgroundGreen text-backgroundGreen'
                              : ''
                          }
                          ${
                            !isSelected && !isToday && isAvailable
                              ? 'text-gray-700 hover:bg-gray-100'
                              : ''
                          }
                          ${!isAvailable ? 'cursor-default text-gray-300' : ''}
                        `}
                            >
                              {day}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Time Frame */}
                    {selectedTimeFrame && (
                      <div className='mt-6'>
                        <h3 className='mb-3 text-sm font-bold text-gray-900'>
                          {t('reschedule.timeFrame')}
                        </h3>
                        <p className='text-sm text-gray-700'>
                          {selectedTimeFrame}
                        </p>
                      </div>
                    )}

                    {/* Submit button */}
                    <button
                      onClick={handleSubmit}
                      disabled={!selectedDateInfo}
                      className='mt-6 w-full rounded-md bg-backgroundGreen py-3 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      {t('reschedule.updateShipment')}
                    </button>
                  </>
                )}

              {footer}
            </>
          )}

          {/* LOADING STATE */}
          {drawerState === 'loading' && (
            <div className='flex flex-1 flex-col'>
              <div className='flex flex-1 flex-col items-center justify-center py-16 text-center'>
                <svg
                  className='animate-spin text-gray-400'
                  width='56'
                  height='56'
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <path
                    d='M20 12a8 8 0 1 1-4.93-7.4'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                  />
                  <path
                    d='M20 4v4h-4'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                <h3 className='mt-6 text-base font-bold text-gray-900'>
                  {t('reschedule.updating')}
                </h3>
                <p className='mt-2 text-sm text-gray-500'>
                  {t('reschedule.confirmingDate', { mode: modeLabel })}
                </p>
              </div>
              {footer}
            </div>
          )}

          {/* SUCCESS STATE */}
          {drawerState === 'success' && (
            <div className='flex flex-1 flex-col'>
              <div className='flex flex-1 flex-col items-center justify-center py-16 text-center'>
                <div className='flex h-24 w-24 items-center justify-center rounded-full bg-green-100'>
                  <svg width='44' height='44' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M20 6L9 17l-5-5'
                      stroke='#22C70D'
                      strokeWidth='2.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <h3 className='mt-8 text-base font-bold text-gray-900'>
                  {t('reschedule.successTitle')}
                </h3>
                <p className='mt-3 text-sm text-gray-500'>
                  {selectedTimeFrame
                    ? t('reschedule.successMessageWithWindow', {
                        date: formatSelected(),
                        window: selectedTimeFrame,
                      })
                    : t('reschedule.successMessage', {
                        date: formatSelected(),
                      })}
                </p>
              </div>
              {footer}
            </div>
          )}

          {/* ERROR STATE */}
          {drawerState === 'error' && (
            <div className='flex flex-1 flex-col'>
              <div className='flex flex-1 flex-col items-center justify-center py-16 text-center'>
                <div className='flex h-24 w-24 items-center justify-center rounded-full bg-red-100'>
                  <svg width='44' height='44' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M18 6L6 18M6 6l12 12'
                      stroke='#DC3434'
                      strokeWidth='2.5'
                      strokeLinecap='round'
                    />
                  </svg>
                </div>
                <h3 className='mt-8 text-base font-bold text-gray-900'>
                  {t('reschedule.errorTitle')}
                </h3>
                <p className='mt-3 text-sm text-gray-500'>
                  {t('reschedule.errorBody1')}
                </p>
                <p className='mt-3 text-sm text-gray-500'>
                  {t('reschedule.errorBody2')}
                </p>
                <button
                  onClick={() => setDrawerState('calendar')}
                  className='mt-6 rounded-md bg-backgroundGreen px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90'
                >
                  {t('reschedule.tryAgain')}
                </button>
              </div>
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
